/**
 * What E4 adds to a quote-class record on a process_engine deployment
 * (spec §9.3): the process band (SA-460) and the four record cards.
 * Everything here is React Query, so the
 * page mounts these ONLY on that lane — the rest of the page (and every
 * other deployment) stays in the hand-rolled `useOrders` lane.
 *
 * SA-447: the cards come in two parts for the record lane's two columns —
 * `RecordWorkCards` (quote versions, the status) in the work column, and
 * `RecordEnquiryCards` (a module's enquiry cards, external references, the
 * original enquiry) in the enquiry column. `RecordWorkModuleCards` is a
 * custom module's work-column cards (`RecordCardSlot`, E5 spec §7.2),
 * mounted separately so a caller can put it LAST in the work column, after
 * every built-in section (SA-461, spec §7.4's table order, ruling R10) —
 * it no longer follows the status card directly.
 */
import { useMemo } from 'react'
import { RecordCardSlot } from '@/components/custom/CustomSlots'
import { useRecordSource, useReferenceSystems, useStatusMap } from '@/hooks/useRecordExtras'
import { birthFacts } from './birthText'
import { RecordProcessBand } from './RecordProcessBand'
import { QuoteVersionsCard } from './QuoteVersionsCard'
import { ReferencesCard } from './ReferencesCard'
import { SourceCard } from './SourceCard'
import { sourceRefOf } from './sourceText'
import { StatusOverrideControl } from './StatusOverrideControl'
import type { Order } from '@/types/commerce'

/** The record's process band (SA-460, spec §7.2), with the record's source for the drawer's conversation step. */
export function RecordProcessSection({
  order, canStart, refreshToken, assigneeNames, selectedStep, onSelectStep, onChanged, landing, onLanded,
}: {
  order: Order
  canStart: boolean
  refreshToken: number
  assigneeNames?: Record<string, string>
  selectedStep: string | null
  onSelectStep: (id: string | null) => void
  onChanged: () => void
  /** SA-1328: land on the current step once (the task page's `?land=process`). */
  landing?: boolean
  onLanded?: () => void
}) {
  const source = useRecordSource(order.id)
  const birth = useMemo(() => birthFacts(order, source.data ?? null), [order, source.data])
  return (
    <RecordProcessBand
      orderId={order.id}
      orderType={order.orderType}
      isOpen={!order.outcome}
      canStart={canStart}
      refreshToken={refreshToken}
      source={sourceRefOf(source.data)}
      birth={birth}
      assigneeNames={assigneeNames}
      selectedStep={selectedStep}
      onSelectStep={onSelectStep}
      onChanged={onChanged}
      customerUpdate={order.customerUpdate ?? null}
      landing={landing}
      onLanded={onLanded}
    />
  )
}

/** The work column's cards: what the record is worth and where it stands. */
export function RecordWorkCards({
  order, focusPanel, onChanged,
}: {
  order: Order
  focusPanel?: string | null
  /** A card wrote something that can move the record or its process. */
  onChanged: () => void
}) {
  const statusMap = useStatusMap(order.id)
  return (
    <>
      <QuoteVersionsCard
        orderId={order.id}
        currency={order.currency}
        open={!order.outcome}
        focused={focusPanel === 'quotes'}
        onChanged={onChanged}
      />
      <StatusOverrideControl
        order={order}
        overrides={statusMap.data?.overrides ?? []}
        labels={statusMap.data?.labels ?? {}}
        focused={focusPanel === 'status'}
        onChanged={onChanged}
      />
    </>
  )
}

/** A custom module's work-column cards, mounted LAST in the work column
 * (spec §7.4's table order, ruling R10) — inside this lane, so a card may
 * use React Query. */
export function RecordWorkModuleCards({ order, onChanged }: { order: Order; onChanged: () => void }) {
  return <RecordCardSlot column="work" order={order} onChanged={onChanged} />
}

/** The enquiry column's cards: a custom module's enquiry cards first (SA-451
 * — the Route card sits above the references, as the wireframe has it), then
 * where the record came from. */
export function RecordEnquiryCards({
  order, focusPanel, onChanged,
}: {
  order: Order
  /** `?panel=<section id>` (SA-461, spec §7.5): every section answers it. */
  focusPanel?: string | null
  onChanged: () => void
}) {
  const systems = useReferenceSystems()
  return (
    <>
      <RecordCardSlot column="enquiry" order={order} onChanged={onChanged} />
      <ReferencesCard orderId={order.id} suggestedSystems={systems.data ?? []} focused={focusPanel === 'references'} />
      <SourceCard orderId={order.id} focused={focusPanel === 'source'} />
    </>
  )
}
